import { FilterValue } from "../../../models/Filters/FilterValue";
import { NewAPIResponse } from "../../../models/NetworkResponse";
import { getAPI } from "../../../api/Networking";
import {
  LeadOwnerLeaderboardResponse,
  LeadOwnerLeaderboardRow,
  LeadStageAveragesResponse,
  LeadStageStatusSummaryResponse,
  LeadStageSummaryListResponse,
  LeadStageSummaryPaginatedResponse,
  LeadStageSummaryRow,
} from "../../../models/Dashboard/LeadAnalytics/LeadAnalytics";
import { LeadAnalyticsFilters } from "../../../models/Dashboard/LeadAnalytics/LeadAnalyticsFilters";

const LEADERBOARD_PAGE_LIMIT = 200;
type LeaderboardStatus = "Won" | "Qualified";

export default class LeadAnalyticsAPI {
  private buildQueryParams = (
    filters: LeadAnalyticsFilters,
    includeCellFilters = false,
  ): URLSearchParams => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.owner_id.length > 0) {
        queryParams.append("owner_id", filters.owner_id.join(","));
      }
      if (filters.pipeline_id.length > 0) {
        queryParams.append("pipeline_id", filters.pipeline_id.join(","));
      }
      if (filters.source_id.length > 0) {
        queryParams.append("source_id", filters.source_id.join(","));
      }
      if (filters.start_date) {
        queryParams.append("start_date", filters.start_date.format("YYYY-MM-DD"));
      }
      if (filters.end_date) {
        queryParams.append("end_date", filters.end_date.format("YYYY-MM-DD"));
      }

      queryParams.append("section", filters.section);
      queryParams.append("page_no", String(filters.page_no));
      queryParams.append("limit", String(filters.limit));
      queryParams.append("sort_by", filters.sort_by);
      queryParams.append("sort_type", filters.sort_type);

      if (includeCellFilters && filters.final_status) {
        queryParams.append("status", filters.final_status);
        queryParams.append("final_status", filters.final_status);
        if (filters.stage_pipeline_id) {
          queryParams.append("stage_pipeline_id", String(filters.stage_pipeline_id));
        }
        if (filters.conversion_probability != null) {
          queryParams.append("conversion_probability", String(filters.conversion_probability));
        }
      }

      return queryParams;
    } catch (error) {
      throw error;
    }
  };

  private buildLeaderboardQueryParams = (
    filters: LeadAnalyticsFilters,
    pageNo: number,
    status?: LeaderboardStatus,
  ): URLSearchParams => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.owner_id.length > 0) {
        queryParams.append("owner_id", filters.owner_id.join(","));
      }
      if (filters.pipeline_id.length > 0) {
        queryParams.append("pipeline_id", filters.pipeline_id.join(","));
      }
      if (filters.source_id.length > 0) {
        queryParams.append("source_id", filters.source_id.join(","));
      }
      if (filters.start_date) {
        queryParams.append("start_date", filters.start_date.format("YYYY-MM-DD"));
      }
      if (filters.end_date) {
        queryParams.append("end_date", filters.end_date.format("YYYY-MM-DD"));
      }

      queryParams.append("section", filters.section);
      if (status) {
        // Same filters used when clicking Won / Qualified in the leaderboard
        queryParams.append("status", status);
        queryParams.append("final_status", status);
      }
      queryParams.append("page_no", String(pageNo));
      queryParams.append("limit", String(LEADERBOARD_PAGE_LIMIT));
      queryParams.append("sort_by", "created_date");
      queryParams.append("sort_type", "desc");

      return queryParams;
    } catch (error) {
      throw error;
    }
  };

  private fetchLeadsForLeaderboard = async (
    filters: LeadAnalyticsFilters,
    status?: LeaderboardStatus,
  ): Promise<LeadStageSummaryRow[]> => {
    try {
      const leads: LeadStageSummaryRow[] = [];
      let pageNo = 1;
      let totalPages = 1;

      while (pageNo <= totalPages) {
        const queryParams = this.buildLeaderboardQueryParams(filters, pageNo, status);
        const response = await getAPI(
          `/v1/lead-management/leads?${queryParams.toString()}`,
        );
        const result = response.result as LeadStageSummaryListResponse;
        leads.push(...(result.list || []));
        totalPages = Math.max(result.total_pages || 1, 1);
        pageNo += 1;

        // Safety cap to avoid runaway pagination
        if (pageNo > 25) {
          break;
        }
      }

      return leads;
    } catch (error) {
      throw error;
    }
  };

  private getOwnerKey = (lead: LeadStageSummaryRow) => {
    if (lead.owner_id != null && String(lead.owner_id).trim() !== "") {
      return `id:${String(lead.owner_id)}`;
    }
    return `name:${(lead.owner_name || "Unknown").trim().toLowerCase() || "unknown"}`;
  };

  private aggregateOwnerLeaderboard = (
    assignedLeads: LeadStageSummaryRow[],
    wonLeads: LeadStageSummaryRow[],
    qualifiedLeads: LeadStageSummaryRow[],
    owners: FilterValue[] = [],
  ): LeadOwnerLeaderboardRow[] => {
    const ownerIdByName = new Map(
      owners.map((owner) => [owner.value.trim().toLowerCase(), String(owner.id)]),
    );
    const ownerNameById = new Map(
      owners.map((owner) => [String(owner.id), owner.value]),
    );
    const ownerMap = new Map<string, LeadOwnerLeaderboardRow>();

    const ensureOwner = (lead: LeadStageSummaryRow) => {
      const ownerKey = this.getOwnerKey(lead);
      const existing = ownerMap.get(ownerKey);
      if (existing) return existing;

      const ownerName = lead.owner_name?.trim() || "Unknown";
      const ownerIdFromLead =
        lead.owner_id != null && String(lead.owner_id).trim() !== ""
          ? String(lead.owner_id)
          : null;
      const ownerId =
        ownerIdFromLead ||
        ownerIdByName.get(ownerName.toLowerCase()) ||
        ownerName;

      const created: LeadOwnerLeaderboardRow = {
        owner_id: ownerId,
        owner_name: ownerNameById.get(String(ownerId)) || ownerName,
        assigned_count: 0,
        won_count: 0,
        qualified_count: 0,
        converted_count: 0,
        conversion_percent: 0,
      };
      ownerMap.set(ownerKey, created);
      return created;
    };

    assignedLeads.forEach((lead) => {
      ensureOwner(lead).assigned_count += 1;
    });

    // Count Won / Qualified from the same status-filtered API used on click
    wonLeads.forEach((lead) => {
      ensureOwner(lead).won_count += 1;
    });

    qualifiedLeads.forEach((lead) => {
      ensureOwner(lead).qualified_count += 1;
    });

    ownerMap.forEach((row) => {
      // Assigned should never be lower than converted for ranking math
      row.assigned_count = Math.max(
        row.assigned_count,
        row.won_count + row.qualified_count,
      );
      row.converted_count = row.won_count + row.qualified_count;
      row.conversion_percent =
        row.assigned_count > 0
          ? Math.round((row.converted_count / row.assigned_count) * 1000) / 10
          : 0;
    });

    return Array.from(ownerMap.values()).sort((a, b) => {
      if (b.conversion_percent !== a.conversion_percent) {
        return b.conversion_percent - a.conversion_percent;
      }
      if (b.converted_count !== a.converted_count) {
        return b.converted_count - a.converted_count;
      }
      if (b.assigned_count !== a.assigned_count) {
        return b.assigned_count - a.assigned_count;
      }
      return a.owner_name.localeCompare(b.owner_name);
    });
  };

  getLeadStageSummary = async (
    filters: LeadAnalyticsFilters,
  ): Promise<LeadStageSummaryPaginatedResponse> => {
    try {
      const listQueryParams = this.buildQueryParams(filters, true);
      const analyticsQueryParams = this.buildQueryParams(filters, false);

      const [listResponse, averagesResponse, summaryResponse] = await Promise.all([
        getAPI(`/v1/lead-management/leads?${listQueryParams.toString()}`),
        getAPI(`/v1/lead-management/lead-stage-averages?${analyticsQueryParams.toString()}`),
        getAPI(
          `/v1/lead-management/lead-stage-status-summary?${analyticsQueryParams.toString()}`,
        ),
      ]);

      const listResult = listResponse.result as LeadStageSummaryListResponse;
      const averagesResult = averagesResponse.result as LeadStageAveragesResponse;
      const summaryResult = summaryResponse.result as LeadStageStatusSummaryResponse;

      return {
        ...listResult,
        averages: averagesResult.averages,
        probability_stage_summary: summaryResult.probability_stage_summary,
        section: summaryResult.section || filters.section,
      };
    } catch (error) {
      throw error;
    }
  };

  getLeadOwnerLeaderboard = async (
    filters: LeadAnalyticsFilters,
    owners: FilterValue[] = [],
  ): Promise<LeadOwnerLeaderboardResponse> => {
    try {
      const [assignedLeads, wonLeads, qualifiedLeads] = await Promise.all([
        this.fetchLeadsForLeaderboard(filters),
        this.fetchLeadsForLeaderboard(filters, "Won"),
        this.fetchLeadsForLeaderboard(filters, "Qualified"),
      ]);

      return {
        list: this.aggregateOwnerLeaderboard(
          assignedLeads,
          wonLeads,
          qualifiedLeads,
          owners,
        ),
      };
    } catch (error) {
      throw error;
    }
  };

  getLeadOwners = async (): Promise<FilterValue[]> => {
    try {
      const response = (await getAPI(
        "/v5/sales-analytics/owners",
      )) as NewAPIResponse<{ list: FilterValue[] }>;

      const owners = response.result?.list || [];
      return owners.map((owner) => ({
        id: String(owner.id),
        value: owner.value,
      }));
    } catch (error) {
      throw error;
    }
  };

  getLeadPipelines = async (): Promise<FilterValue[]> => {
    try {
      const response = (await getAPI(
        "/v1/lead-management/pipelines",
      )) as NewAPIResponse<FilterValue[]>;

      const pipelines = response.result || [];
      return pipelines.map((pipeline) => ({
        id: String(pipeline.id),
        value: pipeline.value,
      }));
    } catch (error) {
      throw error;
    }
  };

  getLeadSources = async (): Promise<FilterValue[]> => {
    try {
      const response = (await getAPI(
        "/v1/lead-management/sources",
      )) as NewAPIResponse<FilterValue[]>;

      const sources = response.result || [];
      return sources.map((source) => ({
        id: String(source.id),
        value: source.value,
      }));
    } catch (error) {
      throw error;
    }
  };
}
